import * as THREE from 'three'
import { CameraMode } from '@/shared/types/core'
import type { PotreeEngine } from '@/engine/PotreeEngine'
import type { GaussianSplatHandle } from '@/shared/types/services'

export interface ProjectInfoCameraSource {
  firstCameraMatrix?: unknown
  coordinateSystem?: unknown
  anchors?: unknown
}

export interface ProjectInfoTransform {
  cameraMatrix?: number[]
  modelMatrix?: number[]
  cameraTarget?: number[]
}

const PROJECT_INFO_RELATIVE_URL = '../project_info.json'
const INITIAL_CAMERA_MATRIX_TARGET_DISTANCE = 10
const PROJECT_INFO_CAMERA_FOV = 65

export async function loadProjectInfoTransform(lodMetaUrl: string): Promise<ProjectInfoTransform | undefined> {
  let projectInfoUrl: string
  try {
    projectInfoUrl = resolveProjectInfoUrl(lodMetaUrl)
  } catch {
    return undefined
  }

  try {
    const response = await fetch(projectInfoUrl)
    if (!response.ok) {
      return undefined
    }
    return parseProjectInfoTransform(await response.json())
  } catch {
    return undefined
  }
}

export function resolveProjectInfoUrl(lodMetaUrl: string): string {
  return new URL(PROJECT_INFO_RELATIVE_URL, lodMetaUrl).toString()
}

export function parseProjectInfoTransform(value: unknown): ProjectInfoTransform | undefined {
  const source = value as ProjectInfoCameraSource | null
  const rowMajorValues = readRowMajorMatrix4(source?.firstCameraMatrix)
  if (!rowMajorValues) {
    return undefined
  }

  const cameraToWorld = new THREE.Matrix4().set(...rowMajorValues)
  const coordinateTransform = resolveCoordinateSystemTransform(source?.coordinateSystem)
  if (!coordinateTransform) {
    return {
      cameraMatrix: Array.from(cameraToWorld.toArray()),
      cameraTarget: parseFirstAnchorTarget(source?.anchors)?.toArray(),
    }
  }

  const cameraMatrix = cameraToWorld
    .premultiply(coordinateTransform.modelMatrix)
    .multiply(coordinateTransform.cameraMatrix)

  return {
    cameraMatrix: Array.from(keepCameraUpright(cameraMatrix).toArray()),
    modelMatrix: Array.from(coordinateTransform.modelMatrix.toArray()),
    cameraTarget: parseFirstAnchorTarget(source?.anchors, coordinateTransform.modelMatrix)?.toArray(),
  }
}

export function applyProjectInfoModelMatrix(
  handle: GaussianSplatHandle,
  transform: ProjectInfoTransform | undefined,
): void {
  if (!transform?.modelMatrix || !handle.object3D) {
    return
  }
  try {
    const matrix = new THREE.Matrix4().fromArray(transform.modelMatrix)
    handle.object3D.applyMatrix4(matrix)
    handle.object3D.updateMatrixWorld(true)
  } catch {
    // Optional metadata should not break loading the model.
  }
}

export function applyProjectInfoInitialCamera(
  engine: PotreeEngine,
  transform: ProjectInfoTransform | undefined,
): boolean {
  return applyInitialCameraMatrix(engine, transform?.cameraMatrix, {
    target: transform?.cameraTarget,
  })
}

export function applyInitialCameraMatrix(
  engine: PotreeEngine,
  cameraMatrix: number[] | undefined,
  options: { target?: THREE.Vector3 | number[] } = {},
): boolean {
  if (!cameraMatrix || cameraMatrix.length !== 16 || !cameraMatrix.every((item) => Number.isFinite(item))) {
    return false
  }
  try {
    const matrix = new THREE.Matrix4().fromArray(cameraMatrix)
    const position = new THREE.Vector3().setFromMatrixPosition(matrix)
    const xAxis = new THREE.Vector3()
    const yAxis = new THREE.Vector3()
    const zAxis = new THREE.Vector3()
    matrix.extractBasis(xAxis, yAxis, zAxis)

    const forward = zAxis.multiplyScalar(-1)
    const up = yAxis
    if (
      !isFiniteVector(position)
      || !isFiniteVector(forward)
      || !isFiniteVector(up)
      || forward.lengthSq() <= 1e-10
      || up.lengthSq() <= 1e-10
    ) {
      return false
    }

    forward.normalize()
    up.normalize()
    const target = readInitialCameraTarget(options.target)
      ?? position.clone().add(forward.multiplyScalar(INITIAL_CAMERA_MATRIX_TARGET_DISTANCE))

    engine.camera.setState({
      ...engine.camera.getState(),
      mode: CameraMode.Perspective,
      fov: PROJECT_INFO_CAMERA_FOV,
      position,
      target,
      up,
    })
    return true
  } catch {
    return false
  }
}

function parseFirstAnchorTarget(value: unknown, transform?: THREE.Matrix4): THREE.Vector3 | undefined {
  if (!Array.isArray(value)) {
    return undefined
  }
  for (const item of value) {
    if (!isRecord(item)) {
      continue
    }
    const position = parseWorldPosition(item.worldPosition)
    if (!position) {
      continue
    }
    return transform ? position.applyMatrix4(transform) : position
  }
  return undefined
}

function parseWorldPosition(value: unknown): THREE.Vector3 | undefined {
  if (!isRecord(value)) {
    return undefined
  }
  const { x, y, z } = value
  if (
    typeof x !== 'number'
    || typeof y !== 'number'
    || typeof z !== 'number'
    || !Number.isFinite(x)
    || !Number.isFinite(y)
    || !Number.isFinite(z)
  ) {
    return undefined
  }
  return new THREE.Vector3(x, y, z)
}

function resolveCoordinateSystemTransform(value: unknown): {
  cameraMatrix: THREE.Matrix4
  modelMatrix: THREE.Matrix4
} | null {
  if (!isRecord(value)) {
    return null
  }
  const convention = normalizeText(value.convention)
  const cameraMatrix = normalizeText(value.cameraMatrix)
  const worldUp = normalizeText(value.worldUp)
  if (convention !== 'opencv' || cameraMatrix !== 'camera_to_world' || worldUp !== '-y') {
    return null
  }

  return {
    cameraMatrix: new THREE.Matrix4().makeScale(1, -1, -1),
    // Matches the viewer runtime compatibility path: the metadata currently says
    // worldUp: -Y, but exported SOG/3DGS models are aligned as +Y-up in practice.
    // Convert that model/anchor space to Share3D's +Z-up world.
    modelMatrix: new THREE.Matrix4().set(
      1, 0, 0, 0,
      0, 0, -1, 0,
      0, 1, 0, 0,
      0, 0, 0, 1,
    ),
  }
}

function readInitialCameraTarget(value: THREE.Vector3 | number[] | undefined): THREE.Vector3 | undefined {
  if (value instanceof THREE.Vector3) {
    return isFiniteVector(value) ? value.clone() : undefined
  }
  if (!Array.isArray(value) || value.length !== 3) {
    return undefined
  }
  const [x, y, z] = value
  if (![x, y, z].every((item) => typeof item === 'number' && Number.isFinite(item))) {
    return undefined
  }
  return new THREE.Vector3(x, y, z)
}

function normalizeText(value: unknown): string {
  return typeof value === 'string' ? value.trim().toLowerCase() : ''
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isFiniteVector(value: THREE.Vector3): boolean {
  return Number.isFinite(value.x) && Number.isFinite(value.y) && Number.isFinite(value.z)
}

function keepCameraUpright(matrix: THREE.Matrix4): THREE.Matrix4 {
  const xAxis = new THREE.Vector3()
  const yAxis = new THREE.Vector3()
  const zAxis = new THREE.Vector3()
  matrix.extractBasis(xAxis, yAxis, zAxis)
  if (yAxis.dot(new THREE.Vector3(0, 0, 1)) >= 0) {
    return matrix
  }

  const position = new THREE.Vector3().setFromMatrixPosition(matrix)
  const forward = zAxis.clone().multiplyScalar(-1).normalize()
  const up = computeRollFreeUp(forward)
  const z = forward.clone().multiplyScalar(-1)
  const x = up.clone().cross(z).normalize()
  return new THREE.Matrix4().makeBasis(x, up, z).setPosition(position)
}

function computeRollFreeUp(direction: THREE.Vector3): THREE.Vector3 {
  const forward = direction.clone()
  if (forward.lengthSq() <= 1e-10) {
    return new THREE.Vector3(0, 0, 1)
  }
  forward.normalize()
  const worldUp = new THREE.Vector3(0, 0, 1)
  const right = new THREE.Vector3().crossVectors(forward, worldUp)
  if (right.lengthSq() <= 1e-10) {
    return new THREE.Vector3(0, 1, 0)
  }
  return new THREE.Vector3().crossVectors(right.normalize(), forward).normalize()
}

function readRowMajorMatrix4(value: unknown): [
  number, number, number, number,
  number, number, number, number,
  number, number, number, number,
  number, number, number, number,
] | undefined {
  if (!Array.isArray(value)) {
    return undefined
  }

  const values = Array.isArray(value[0])
    ? value.flatMap((row) => Array.isArray(row) ? row : [])
    : value
  if (values.length !== 16 || !values.every((item) => typeof item === 'number' && Number.isFinite(item))) {
    return undefined
  }

  return values as [
    number, number, number, number,
    number, number, number, number,
    number, number, number, number,
    number, number, number, number,
  ]
}
